import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  CalendarClock,
  TrendingUp,
  Map as MapIcon,
  Route,
  MountainSnow,
  Flame,
  CalendarRange,
  ArrowUp,
  ArrowDown,
  Landmark,
  Repeat,
} from "lucide-react";
import { AtlasPanel } from "./AtlasPanel";
import { ProgressBar } from "./ProgressBar";
import { StatBlock } from "./StatBlock";
import { EmptyState } from "./EmptyState";
import { Skel } from "./Skeleton";
import { ProGate } from "./ProGate";
import { useIsPro } from "../hooks/useIsPro";
import {
  fetchAnalyticsTrends,
  fetchAnalyticsBreakdown,
  fetchAnalyticsProjections,
  fetchAnalyticsRecords,
  fetchAnalyticsVisits,
} from "../api/analytics";
import type {
  AnalyticsTrendBucket,
  AnalyticsProjection,
  AnalyticsRegionBreakdownRow,
  AnalyticsRecords,
  AnalyticsVisits,
  AnalyticsVisitMoment,
} from "../api/analytics";

// ANA-2 — the "Insights" group on StatisticsPage. Completionist analytics:
// monthly trends, completion projections, region breakdown. Charts are
// hand-rolled SVG (no chart library). Free users see the whole section behind
// one <ProGate> with seeded-fake data as the preview, so no Pro endpoint is
// called for them (see specs/07-advanced-analytics.md).

// ─── Monthly trends ───────────────────────────────────────────────────────────

type Metric = "distance" | "elevation" | "summits";

const METRICS: Record<Metric, { label: string; pick: (b: AnalyticsTrendBucket) => number; fmt: (v: number) => string }> = {
  distance: { label: "Distance", pick: (b) => b.distanceM, fmt: (v) => `${Math.round(v / 1000)} km` },
  elevation: { label: "Elevation", pick: (b) => b.elevationGainM, fmt: (v) => `${Math.round(v)} m` },
  summits: { label: "Summits", pick: (b) => b.summits, fmt: (v) => String(v) },
};

/** Non-distorting bar chart. Each bar is positioned in its own even column and
 * scaled by value; labels are HTML underneath so text never stretches. */
function BarChart({ buckets, metric }: { buckets: AnalyticsTrendBucket[]; metric: Metric }) {
  const { pick, fmt } = METRICS[metric];
  const values = buckets.map(pick);
  const max = Math.max(1, ...values);
  const n = Math.max(1, buckets.length);
  const H = 100;
  const slot = 10;
  const W = n * slot;
  // Show ~6 labels evenly so a 24-month axis doesn't crowd.
  const labelStep = Math.max(1, Math.round(n / 6));

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        width="100%"
        height={150}
        style={{ display: "block" }}
        role="img"
        aria-label={`${METRICS[metric].label} by month`}
      >
        {buckets.map((b, i) => {
          const v = pick(b);
          const h = (v / max) * H;
          return (
            <rect key={b.bucket} x={i * slot + 1} y={H - h} width={slot - 2} height={h} fill="var(--sky-500)" rx={0.6}>
              <title>{`${b.bucket} · ${fmt(v)}`}</title>
            </rect>
          );
        })}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        {buckets.map((b, i) =>
          i % labelStep === 0 ? (
            <span key={b.bucket} style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)" }}>
              {formatBucketLabel(b.bucket)}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}

function TrendsPanel({ buckets }: { buckets: AnalyticsTrendBucket[] }) {
  const [metric, setMetric] = React.useState<Metric>("distance");
  const hasData = buckets.some((b) => b.activities > 0);
  return (
    <AtlasPanel
      title="Monthly trends"
      action={
        <select
          aria-label="Trend metric"
          value={metric}
          onChange={(e) => setMetric(e.target.value as Metric)}
          style={{
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            color: "var(--text-secondary)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            padding: "4px 8px",
          }}
        >
          {(Object.keys(METRICS) as Metric[]).map((m) => (
            <option key={m} value={m}>
              {METRICS[m].label}
            </option>
          ))}
        </select>
      }
    >
      {hasData ? (
        <BarChart buckets={buckets} metric={metric} />
      ) : (
        <EmptyState
          icon={<TrendingUp size={18} />}
          title="No trend data yet"
          hint="Import activities to see how your months compare."
        />
      )}
    </AtlasPanel>
  );
}

// ─── Completion projections ───────────────────────────────────────────────────

function ProjectionsPanel({ projections }: { projections: AnalyticsProjection[] }) {
  return (
    <AtlasPanel title="Completion projections">
      {projections.length === 0 ? (
        <EmptyState
          icon={<CalendarClock size={18} />}
          title="No collections in progress"
          hint="Start collecting peaks or trails and Atlas will forecast when you'll finish."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {projections.map((p) => (
            <div key={p.slug}>
              <ProgressBar
                label={p.name}
                value={p.collected}
                max={p.total}
                color="gold"
                valueFormat={(v, m) => `${v} / ${m} · ${m - v} to go`}
              />
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>
                {p.projectedAt ? (
                  <>
                    At {formatRate(p.perMonth)} {unitLabel(p.type)} a month · complete{" "}
                    <span style={{ color: "var(--gold-300)", fontWeight: 600 }}>
                      {formatMonthYear(p.projectedAt)}
                    </span>
                  </>
                ) : (
                  "No recent progress — get out there"
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AtlasPanel>
  );
}

// ─── Region breakdown ─────────────────────────────────────────────────────────

function RegionPanel({ regions }: { regions: AnalyticsRegionBreakdownRow[] }) {
  return (
    <AtlasPanel title="Region breakdown">
      {regions.length === 0 ? (
        <EmptyState
          icon={<MapIcon size={18} />}
          title="No regions covered yet"
          hint="Your covered regions and how much of each you've explored will appear here."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {regions.map((r) => (
            <div key={r.regionId}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6, gap: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.name}
                </span>
                <span className="eyebrow" style={{ fontSize: 10, color: "var(--text-secondary)", flexShrink: 0 }}>
                  {Math.round(r.coveragePct)}% · {r.summits} summits
                  {r.summitsThisYear > 0 && (
                    <span style={{ color: "var(--spruce-400)" }}> · +{r.summitsThisYear} this year</span>
                  )}
                </span>
              </div>
              <ProgressBar value={r.coveragePct} max={100} color="spruce" size="sm" showValue={false} />
            </div>
          ))}
        </div>
      )}
    </AtlasPanel>
  );
}

// ─── Personal records ─────────────────────────────────────────────────────────

interface RecordCell {
  key: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  activityId?: string | null;
}

function recordCells(r: AnalyticsRecords): RecordCell[] {
  const cells: RecordCell[] = [];
  if (r.longestActivity) {
    cells.push({
      key: "longest",
      icon: <Route size={12} />,
      label: "Longest outing",
      value: `${r.longestActivity.distanceKm} km`,
      sub: `${r.longestActivity.name} · ${formatDate(r.longestActivity.date)}`,
      activityId: r.longestActivity.activityId,
    });
  }
  if (r.biggestAscent) {
    cells.push({
      key: "ascent",
      icon: <TrendingUp size={12} />,
      label: "Biggest ascent",
      value: `${(r.biggestAscent.elevationGainM ?? 0).toLocaleString()} m`,
      sub: `${r.biggestAscent.name} · ${formatDate(r.biggestAscent.date)}`,
      activityId: r.biggestAscent.activityId,
    });
  }
  if (r.highestSummit) {
    cells.push({
      key: "highest",
      icon: <MountainSnow size={12} />,
      label: "Highest summit",
      value: `${r.highestSummit.elevationM.toLocaleString()} m`,
      sub: `${r.highestSummit.name} · ${formatDate(r.highestSummit.summitedAt)}`,
      activityId: r.highestSummit.activityId,
    });
  }
  if (r.mostSummitsDay) {
    cells.push({
      key: "day",
      icon: <MountainSnow size={12} />,
      label: "Most summits, one day",
      value: String(r.mostSummitsDay.count),
      sub: formatDate(r.mostSummitsDay.date),
    });
  }
  if (r.mostSummitsMonth) {
    cells.push({
      key: "month",
      icon: <CalendarRange size={12} />,
      label: "Most summits, one month",
      value: String(r.mostSummitsMonth.count),
      sub: formatMonthYear(`${r.mostSummitsMonth.month}-01`),
    });
  }
  if (r.longestStreakDays > 0) {
    cells.push({
      key: "streak",
      icon: <Flame size={12} />,
      label: "Longest streak",
      value: `${r.longestStreakDays} days`,
      sub: "Consecutive active days",
    });
  }
  return cells;
}

function RecordsPanel({ records }: { records: AnalyticsRecords }) {
  const cells = recordCells(records);
  return (
    <AtlasPanel title="Personal records">
      {cells.length === 0 ? (
        <EmptyState
          icon={<TrendingUp size={18} />}
          title="No records yet"
          hint="Your biggest days and highest summits will show up here."
        />
      ) : (
        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {cells.map((cell) => {
            const inner = (
              <div
                style={{
                  background: "var(--surface-sunken)",
                  border: "1px solid var(--border-gold)",
                  borderRadius: "var(--radius-lg)",
                  padding: 14,
                  height: "100%",
                  boxSizing: "border-box",
                }}
              >
                <StatBlock size="sm" gold label={cell.label} icon={cell.icon} value={cell.value} sub={cell.sub} />
              </div>
            );
            return cell.activityId ? (
              <Link key={cell.key} to="/activities/$id/review" params={{ id: cell.activityId }} style={{ textDecoration: "none" }}>
                {inner}
              </Link>
            ) : (
              <div key={cell.key}>{inner}</div>
            );
          })}
        </div>
      )}
    </AtlasPanel>
  );
}

// ─── Year vs year ─────────────────────────────────────────────────────────────

function YearVsYearPanel({ buckets }: { buckets: AnalyticsTrendBucket[] }) {
  const now = new Date();
  const thisYear = String(now.getFullYear());
  const lastYear = String(now.getFullYear() - 1);
  const cur = buckets.find((b) => b.bucket === thisYear);
  const prev = buckets.find((b) => b.bucket === lastYear);
  const cutoffLabel = now.toLocaleDateString(undefined, { day: "numeric", month: "long" });

  const rows =
    cur && prev
      ? [
          { label: "Summits", a: prev.summits, b: cur.summits, fmt: (v: number) => String(v) },
          { label: "Days out", a: prev.daysOut, b: cur.daysOut, fmt: (v: number) => String(v) },
          {
            label: "Distance",
            a: prev.distanceM / 1000,
            b: cur.distanceM / 1000,
            fmt: (v: number) => `${Math.round(v)} km`,
          },
        ]
      : [];

  return (
    <AtlasPanel
      title="This year vs last"
      action={<span className="eyebrow" style={{ fontSize: 10 }}>by {cutoffLabel}</span>}
    >
      {rows.length === 0 ? (
        <EmptyState
          icon={<CalendarRange size={18} />}
          title="Not enough history yet"
          hint="Once you've logged activity across two years, Atlas compares them at the same point in each."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {rows.map((row) => {
            const delta = row.b - row.a;
            return (
              <div key={row.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <span style={{ fontSize: 13, color: "var(--text-secondary)", flex: "0 0 auto", minWidth: 90 }}>{row.label}</span>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flex: 1, justifyContent: "flex-end" }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
                    {lastYear} {row.fmt(row.a)}
                  </span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>
                    {thisYear} {row.fmt(row.b)}
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                      fontSize: 12,
                      fontWeight: 600,
                      color: delta >= 0 ? "var(--spruce-400)" : "var(--text-muted)",
                      minWidth: 42,
                      justifyContent: "flex-end",
                    }}
                  >
                    {delta === 0 ? (
                      "—"
                    ) : (
                      <>
                        {delta > 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                        {row.fmt(Math.abs(delta))}
                      </>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AtlasPanel>
  );
}

// ─── Visits (ATL-147) ─────────────────────────────────────────────────────────
// From the repeat-visit tables: one visit per item per London day. The plain
// count on an item page is free; these lifetime views are the Pro part.

function itemPath(itemType: "peak" | "landmark"): "/peaks/$slug" | "/landmarks/$slug" {
  return itemType === "peak" ? "/peaks/$slug" : "/landmarks/$slug";
}

function ItemLink({ itemType, slug, name }: { itemType: "peak" | "landmark"; slug: string; name: string }) {
  return (
    <Link
      to={itemPath(itemType)}
      params={{ slug }}
      style={{ color: "var(--text-primary)", fontWeight: 600, textDecoration: "none" }}
    >
      {name}
    </Link>
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

function VisitYearBars({ perYear }: { perYear: AnalyticsVisits["perYear"] }) {
  const max = Math.max(1, ...perYear.map((y) => y.peakVisits + y.landmarkVisits));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {perYear.map((y) => {
        const total = y.peakVisits + y.landmarkVisits;
        return (
          <div key={y.year} style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span className="tnum" style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", width: 36, flexShrink: 0 }}>
              {y.year}
            </span>
            <div
              style={{ flex: 1, minWidth: 0, display: "flex", height: 8, borderRadius: 4, background: "var(--surface-sunken)", overflow: "hidden" }}
              title={`${y.year} · ${plural(y.peakVisits, "summit visit", "summit visits")} · ${plural(y.landmarkVisits, "landmark visit", "landmark visits")}`}
            >
              <div style={{ width: `${(y.peakVisits / max) * 100}%`, background: "var(--sky-500)" }} />
              <div style={{ width: `${(y.landmarkVisits / max) * 100}%`, background: "var(--spruce-400)" }} />
            </div>
            <span className="tnum" style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-secondary)", width: 40, textAlign: "right", flexShrink: 0 }}>
              {total.toLocaleString()}
            </span>
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 14, marginTop: 2, fontSize: 11, color: "var(--text-muted)" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: "var(--sky-500)" }} /> Summits
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: "var(--spruce-400)" }} /> Landmarks
        </span>
      </div>
    </div>
  );
}

function VisitMomentLine({ label, moment }: { label: string; moment: AnalyticsVisitMoment }) {
  return (
    <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
      <span style={{ color: "var(--text-muted)" }}>{label}</span>{" "}
      <ItemLink itemType={moment.itemType} slug={moment.slug} name={moment.name} />{" "}
      <span className="tnum" style={{ color: "var(--text-muted)" }}>· {formatDate(moment.visitedAt)}</span>
    </div>
  );
}

function VisitsPanel({ visits }: { visits: AnalyticsVisits }) {
  const { totals, perYear, mostVisited, first, latest } = visits;
  const hasVisits = totals.peakVisits + totals.landmarkVisits > 0;
  return (
    <AtlasPanel title="Visits">
      {!hasVisits ? (
        <EmptyState
          icon={<Repeat size={18} />}
          title="No visits on the record yet"
          hint="Visits appear once an activity or check-in reaches a summit or landmark."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
          <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <StatBlock
              size="sm"
              icon={<MountainSnow size={12} />}
              label="Summit visits"
              value={<span className="tnum">{totals.peakVisits.toLocaleString()}</span>}
              sub={plural(totals.distinctPeaks, "summit", "summits")}
            />
            <StatBlock
              size="sm"
              icon={<Landmark size={12} />}
              label="Landmark visits"
              value={<span className="tnum">{totals.landmarkVisits.toLocaleString()}</span>}
              sub={plural(totals.distinctLandmarks, "landmark", "landmarks")}
            />
            <StatBlock
              size="sm"
              icon={<Repeat size={12} />}
              label="Places you've returned to"
              value={<span className="tnum">{(totals.repeatPeaks + totals.repeatLandmarks).toLocaleString()}</span>}
              sub="Visited on two or more days"
            />
          </div>

          {perYear.length > 0 && (
            <div>
              <p className="eyebrow" style={{ fontSize: 10, margin: "0 0 10px" }}>Visits per year</p>
              <VisitYearBars perYear={perYear} />
            </div>
          )}

          {mostVisited.length > 0 && (
            <div>
              <p className="eyebrow" style={{ fontSize: 10, margin: "0 0 10px" }}>Most visited</p>
              <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                {mostVisited.map((item) => (
                  <li key={`${item.itemType}-${item.id}`} style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <ItemLink itemType={item.itemType} slug={item.slug} name={item.name} />
                    </span>
                    <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {plural(item.visitCount, "visit", "visits")} · first {formatMonthYearShort(item.firstVisitedAt)} · latest{" "}
                      {formatMonthYearShort(item.lastVisitedAt)}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {(first || latest) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {first && <VisitMomentLine label="First visit" moment={first} />}
              {latest && <VisitMomentLine label="Latest visit" moment={latest} />}
            </div>
          )}
        </div>
      )}
    </AtlasPanel>
  );
}

// ─── Live (Pro) & preview (free) ──────────────────────────────────────────────

function todayMonthDay(): string {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function LiveInsights() {
  const trendsQuery = useQuery({
    queryKey: ["analytics/trends", "month"],
    queryFn: () => fetchAnalyticsTrends("month"),
  });
  const projectionsQuery = useQuery({
    queryKey: ["analytics/projections"],
    queryFn: fetchAnalyticsProjections,
  });
  const regionQuery = useQuery({
    queryKey: ["analytics/breakdown", "region"],
    queryFn: () => fetchAnalyticsBreakdown("region"),
  });
  const recordsQuery = useQuery({
    queryKey: ["analytics/records"],
    queryFn: fetchAnalyticsRecords,
  });
  const cutoff = todayMonthDay();
  const yearTrendsQuery = useQuery({
    queryKey: ["analytics/trends", "year", cutoff],
    queryFn: () => fetchAnalyticsTrends("year", cutoff),
  });
  const visitsQuery = useQuery({
    queryKey: ["analytics/visits"],
    queryFn: fetchAnalyticsVisits,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {trendsQuery.data ? <TrendsPanel buckets={trendsQuery.data.buckets} /> : <Skel height="220px" />}
      {projectionsQuery.data ? (
        <ProjectionsPanel projections={projectionsQuery.data.projections} />
      ) : (
        <Skel height="180px" />
      )}
      {recordsQuery.data ? <RecordsPanel records={recordsQuery.data.records} /> : <Skel height="160px" />}
      {yearTrendsQuery.data ? (
        <YearVsYearPanel buckets={yearTrendsQuery.data.buckets} />
      ) : (
        <Skel height="160px" />
      )}
      {regionQuery.data ? <RegionPanel regions={regionQuery.data.regions ?? []} /> : <Skel height="180px" />}
      {visitsQuery.data ? <VisitsPanel visits={visitsQuery.data.visits} /> : <Skel height="200px" />}
    </div>
  );
}

function PreviewInsights() {
  const { buckets, projections, regions, records, yearBuckets, visits } = React.useMemo(seededPreview, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <TrendsPanel buckets={buckets} />
      <ProjectionsPanel projections={projections} />
      <RecordsPanel records={records} />
      <YearVsYearPanel buckets={yearBuckets} />
      <RegionPanel regions={regions} />
      <VisitsPanel visits={visits} />
    </div>
  );
}

export function InsightsSection() {
  const { isPro } = useIsPro();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Pro</p>
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>Insights</h2>
      </div>
      {isPro ? (
        <LiveInsights />
      ) : (
        <ProGate label="Completion forecasts & lifetime trends are a Pro feature" preview={<PreviewInsights />}>
          {/* Never mounted for free users; Pro users take the branch above. */}
          <LiveInsights />
        </ProGate>
      )}
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatBucketLabel(bucket: string): string {
  // "YYYY-MM" → "Jul", "YYYY" → "YYYY".
  const m = /^(\d{4})-(\d{2})$/.exec(bucket);
  if (!m) return bucket;
  const month = parseInt(m[2]!, 10) - 1;
  return new Date(2000, month, 1).toLocaleDateString(undefined, { month: "short" });
}

function formatMonthYear(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function formatMonthYearShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function formatRate(perMonth: number): string {
  const rounded = Math.round(perMonth * 10) / 10;
  const n = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${n}`;
}

/** The item noun for a collection type, used as the run-rate unit —
 * "At 3 peaks a month" (mirrors the spec's "3 fells a month" headline). */
function unitLabel(type: string): string {
  switch (type) {
    case "peaks":
      return "peaks";
    case "trails":
      return "trails";
    case "landmarks":
      return "landmarks";
    case "regions":
      return "regions";
    default:
      return "items";
  }
}

// Deterministic fake data for the free-user preview — same seeded-RNG pattern as
// LandingPage. No network call, so no 402 for free users.
function mulberry32(a: number) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededPreview(): {
  buckets: AnalyticsTrendBucket[];
  projections: AnalyticsProjection[];
  regions: AnalyticsRegionBreakdownRow[];
  records: AnalyticsRecords;
  yearBuckets: AnalyticsTrendBucket[];
  visits: AnalyticsVisits;
} {
  const rnd = mulberry32(73);
  const now = new Date();
  const buckets: AnalyticsTrendBucket[] = Array.from({ length: 24 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 23 + i, 1));
    const activities = Math.round(rnd() * 6) + 1;
    return {
      bucket: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`,
      activities,
      daysOut: activities + Math.round(rnd() * 3),
      distanceM: Math.round((8 + rnd() * 60) * 1000),
      elevationGainM: Math.round(300 + rnd() * 2200),
      summits: Math.round(rnd() * 7),
      points: Math.round(rnd() * 400),
    };
  });

  const emptyYear = (year: number, over: Partial<AnalyticsTrendBucket>): AnalyticsTrendBucket => ({
    bucket: String(year),
    activities: 0,
    daysOut: 0,
    distanceM: 0,
    elevationGainM: 0,
    summits: 0,
    points: 0,
    ...over,
  });
  const yearBuckets: AnalyticsTrendBucket[] = [
    emptyYear(now.getFullYear() - 1, { summits: 41, daysOut: 48, distanceM: 612_000 }),
    emptyYear(now.getFullYear(), { summits: 55, daysOut: 57, distanceM: 738_000 }),
  ];

  const records: AnalyticsRecords = {
    longestActivity: { activityId: null, name: "Fairfield Horseshoe", date: monthsAhead(now, -3), distanceKm: 34.2 },
    biggestAscent: { activityId: null, name: "Ben Nevis via CMD Arête", date: monthsAhead(now, -8), elevationGainM: 1620 },
    highestSummit: { peakSlug: "ben-nevis", name: "Ben Nevis", elevationM: 1345, summitedAt: monthsAhead(now, -8), activityId: null },
    mostSummitsDay: { count: 8, date: monthsAhead(now, -5) },
    mostSummitsMonth: { count: 21, month: `${now.getFullYear()}-${String(Math.max(1, now.getMonth())).padStart(2, "0")}` },
    longestStreakDays: 11,
  };

  const projections: AnalyticsProjection[] = [
    { slug: "wainwrights", name: "Wainwrights", type: "peaks", total: 214, collected: 121, remaining: 93, perMonth: 3, projectedAt: monthsAhead(now, 31) },
    { slug: "munros", name: "Munros", type: "peaks", total: 282, collected: 64, remaining: 218, perMonth: 2, projectedAt: monthsAhead(now, 109) },
    { slug: "national-trails", name: "National Trails", type: "trails", total: 16, collected: 3, remaining: 13, perMonth: 0, projectedAt: null },
  ];

  const regions: AnalyticsRegionBreakdownRow[] = [
    { regionId: "r1", name: "Lake District", slug: "lake-district", coveragePct: 68, summits: 147, summitsThisYear: 22 },
    { regionId: "r2", name: "Snowdonia", slug: "snowdonia", coveragePct: 41, summits: 38, summitsThisYear: 9 },
    { regionId: "r3", name: "Brecon Beacons", slug: "brecon-beacons", coveragePct: 86, summits: 24, summitsThisYear: 0 },
    { regionId: "r4", name: "Dartmoor", slug: "dartmoor", coveragePct: 23, summits: 11, summitsThisYear: 4 },
  ];

  const thisYear = now.getFullYear();
  const visits: AnalyticsVisits = {
    totals: { peakVisits: 186, landmarkVisits: 42, distinctPeaks: 131, distinctLandmarks: 30, repeatPeaks: 27, repeatLandmarks: 8 },
    perYear: [
      { year: thisYear - 3, peakVisits: 31, landmarkVisits: 6 },
      { year: thisYear - 2, peakVisits: 44, landmarkVisits: 9 },
      { year: thisYear - 1, peakVisits: 58, landmarkVisits: 15 },
      { year: thisYear, peakVisits: 53, landmarkVisits: 12 },
    ],
    mostVisited: [
      { itemType: "peak", id: "v1", slug: "cat-bells", name: "Cat Bells", visitCount: 7, firstVisitedAt: monthsAhead(now, -40), lastVisitedAt: monthsAhead(now, -1) },
      { itemType: "peak", id: "v2", slug: "helvellyn", name: "Helvellyn", visitCount: 5, firstVisitedAt: monthsAhead(now, -38), lastVisitedAt: monthsAhead(now, -4) },
      { itemType: "landmark", id: "v3", slug: "castlerigg-stone-circle", name: "Castlerigg Stone Circle", visitCount: 4, firstVisitedAt: monthsAhead(now, -30), lastVisitedAt: monthsAhead(now, -2) },
    ],
    first: { itemType: "peak", id: "v4", slug: "skiddaw", name: "Skiddaw", visitedAt: monthsAhead(now, -44) },
    latest: { itemType: "peak", id: "v1", slug: "cat-bells", name: "Cat Bells", visitedAt: monthsAhead(now, -1) },
  };

  return { buckets, projections, regions, records, yearBuckets, visits };
}

function monthsAhead(now: Date, months: number): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + months, 1)).toISOString();
}
